import React from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Image } from "@UI/Components/Image/Image";
import { Icon } from "@UI/Components/Icon/Icon";
import { Link } from "@UI/Components/Link/Link";
import { FaceViewfinderIcon } from "@UI/Components/Icons/FaceViewfinderIcon";
import { CircleCheck } from "@UI/Components/Icons/CircleCheck";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";
import { useDataQueryClient } from "@Libs/DataLayer/hooks/useDataQuery";
import { IExtendedMetric } from "@modules/ServiceProviders/types/serviceProvider.types";
import { ID } from "@types";
import { requestProfilePictureValidation } from "@modules/ServiceProviders/services/serviceProviders";
import messages from "./messages";

type TProfilePicValue =
  | "missing"
  | "not_requested"
  | "done"
  | "pending"
  | "refused"
  | null;

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  metric: IExtendedMetric | undefined;
  serviceProviderId: ID | undefined;
}

const boldBlack = (...chunks: React.ReactNode[]) => (
  <Text as="span" fontWeight="700" color="gray.900">
    {chunks}
  </Text>
);

const AvatarWithBorder = ({
  imageUrl,
  dashed,
  pending,
  gap = "4px",
}: {
  imageUrl: string;
  dashed?: boolean;
  pending?: boolean;
  gap?: string;
}) => (
  <Box
    w="84px"
    h="84px"
    rounded="full"
    border="2px"
    borderStyle={dashed ? "dashed" : "solid"}
    borderColor="gray.700"
    p={gap}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Box position="relative" w="full" h="full" rounded="full" overflow="hidden">
      <Image
        src={imageUrl}
        alt=""
        w="full"
        h="full"
        objectFit="cover"
        filter={pending ? "saturate(0)" : undefined}
      />
      {pending && (
        <Box position="absolute" inset={0} bg="orange.500" opacity={0.3} />
      )}
    </Box>
  </Box>
);

const PlainAvatar = ({ imageUrl }: { imageUrl: string }) => (
  <Box w="84px" h="84px" rounded="full" overflow="hidden">
    <Image src={imageUrl} alt="" w="full" h="full" objectFit="cover" />
  </Box>
);

export const SuperTalentProfilePictureModal = ({
  isOpen,
  onClose,
  metric,
  serviceProviderId,
}: IProps) => {
  const { generateRoute } = useRoutes();
  const queryClient = useDataQueryClient();
  const value = (metric?.value ?? null) as TProfilePicValue;
  const imageUrl = value === "missing" ? undefined : metric?.computationDetails?.profilePictureThumbUrl as string | undefined;

  const { mutate: submitForApproval, isLoading: isSubmitting } =
    useDataMutation(() => requestProfilePictureValidation(serviceProviderId), {
      onSuccess: () => {
        queryClient.invalidateQueries(["superTalent", Number(serviceProviderId)]);
        onClose();
      },
    });

  const profileUrl = serviceProviderId
    ? generateRoute(["SERVICE_PROVIDER_PROFILE"], {
        id: String(serviceProviderId),
      })
    : "#";

  const handleGoToProfile = () => {
    window.location.href = profileUrl;
  };

  const contactSupportLink = (...chunks: React.ReactNode[]) => (
    <Link href={generateRoute(["CONTACT_US"])} color="pink.500" fontWeight="500">
      {chunks}
    </Link>
  );

  const renderContent = () => {
    // empty: no photo uploaded yet, or previous photo was refused
    if (
      value === "missing" ||
      value === "refused" ||
      (value !== "not_requested" && value !== "pending" && value !== "done")
    ) {
      return (
        <VStack spacing={4} align="stretch">
          <Heading as="h3" size="md">
            <FormattedMessage {...messages.profilePicModalEmptyLine1} />
          </Heading>
          <Flex
            w="80px"
            h="80px"
            rounded="full"
            bg="gray.100"
            align="center"
            justify="center"
            alignSelf="center"
          >
            <Icon as={FaceViewfinderIcon} boxSize="40px" color="icon" />
          </Flex>
          <Text color="gray.700">
            <FormattedMessage
              {...messages.profilePicModalEmptyLine2}
              values={{ bold: boldBlack }}
            />
          </Text>
          <Text color="gray.700">
            <FormattedMessage {...messages.profilePicModalEmptyLine3} />
          </Text>
          <Button colorScheme="seaGreen" onClick={handleGoToProfile} w="full" mt={2}>
            <FormattedMessage {...messages.profilePicModalGoToProfile} />
          </Button>
        </VStack>
      );
    }

    // not validated yet: has photo, not submitted
    if (value === "not_requested") {
      return (
        <VStack spacing={4} align="stretch">
          <Heading as="h3" size="md">
            <FormattedMessage {...messages.profilePicModalNotValidatedTitle} />
          </Heading>
          {imageUrl && (
            <Flex justify="center">
              <AvatarWithBorder imageUrl={imageUrl} dashed />
            </Flex>
          )}
          <Text color="gray.600">
            <FormattedMessage {...messages.profilePicModalReminder} />
          </Text>
          <Flex gap={3} direction="column" mt={2}>
            <Button variant="outline" onClick={handleGoToProfile} w="full">
              <FormattedMessage {...messages.profilePicModalModifyPhoto} />
            </Button>
            <Button
              colorScheme="seaGreen"
              onClick={() => submitForApproval(undefined)}
              isLoading={isSubmitting}
              w="full"
            >
              <FormattedMessage {...messages.profilePicModalSubmit} />
            </Button>
          </Flex>
        </VStack>
      );
    }

    // pending: under review
    if (value === "pending") {
      return (
        <VStack spacing={4} align="stretch">
          <Heading as="h3" size="md">
            <FormattedMessage {...messages.profilePicModalPendingTitle} />
          </Heading>
          {imageUrl && (
            <Flex justify="center">
              <AvatarWithBorder imageUrl={imageUrl} dashed pending />
            </Flex>
          )}
          <Text color="gray.600">
            <FormattedMessage
              {...messages.profilePicModalPendingText}
              values={{ link: contactSupportLink }}
            />
          </Text>
          <Button variant="outline" onClick={onClose} w="full" mt={2}>
            <FormattedMessage {...messages.profilePicModalClose} />
          </Button>
        </VStack>
      );
    }

    // done: approved
    return (
      <VStack spacing={4} align="stretch">
        <Heading as="h3" size="md">
          <FormattedMessage {...messages.profilePicModalDoneTitle} />
        </Heading>
        {imageUrl ? (
          <Flex justify="center">
            <PlainAvatar imageUrl={imageUrl} />
          </Flex>
        ) : (
          <Flex
            w="80px"
            h="80px"
            rounded="full"
            bg="green.50"
            align="center"
            justify="center"
            alignSelf="center"
          >
            <Icon as={CircleCheck} boxSize="40px" color="seaGreen.500" />
          </Flex>
        )}
        <Text color="gray.600">
          <FormattedMessage {...messages.profilePicModalDoneDesc} />
        </Text>
        <Button variant="outline" onClick={onClose} w="full" mt={2}>
          <FormattedMessage {...messages.profilePicModalClose} />
        </Button>
      </VStack>
    );
  };

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} bodyPadding="24px">
      {renderContent()}
    </ResponsiveModal>
  );
};
